// src/admin-components/Subscriptions.jsx
import React, { useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import { getSubscriptions, cancelSubscription } from "../api/adminApi";
import "./admin-dashboard.css";

const STATUS_COLORS = {
  active:    { bg: "#D1FAE5", color: "#065F46" },
  expired:   { bg: "#FEF3C7", color: "#92400E" },
  cancelled: { bg: "#F3F4F6", color: "#6B7280" },
};

// ── Cancel Confirmation Modal ─────────────────────────────
function CancelModal({ sub, onConfirm, onClose, loading }) {
  if (!sub) return null;
  const fmt = (n) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(n || 0);
  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,.45)",
        zIndex: 2000, backdropFilter: "blur(3px)"
      }} />

      {/* Modal */}
      <div style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%,-50%)",
        background: "#fff", borderRadius: 20, padding: 32,
        width: "100%", maxWidth: 440, zIndex: 2001,
        boxShadow: "0 30px 80px rgba(0,0,0,.25)", boxSizing: "border-box"
      }}>
        {/* Icon */}
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{
            width: 64, height: 64, borderRadius: "50%",
            background: "#FEF2F2", border: "2px solid #FECACA",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 28, margin: "0 auto 12px"
          }}>🚫</div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "#1E293B" }}>
            Cancel Subscription?
          </h2>
          <p style={{ margin: "6px 0 0", fontSize: 14, color: "#64748B" }}>
            This action cannot be undone.
          </p>
        </div>

        {/* Subscription details */}
        <div style={{
          background: "#F8FAFC", border: "1px solid #E2E8F0",
          borderRadius: 14, padding: "16px", marginBottom: 20
        }}>
          {[
            { label: "User",    value: sub.user },
            { label: "Phone",   value: sub.phone },
            { label: "Plan",    value: sub.plan },
            { label: "Amount",  value: fmt(sub.amount) },
            { label: "Expires", value: fmtDate(sub.end) },
          ].map(({ label, value }) => (
            <div key={label} style={{
              display: "flex", justifyContent: "space-between",
              padding: "7px 0", borderBottom: "1px solid #E2E8F0",
              fontSize: 14
            }}>
              <span style={{ color: "#64748B" }}>{label}</span>
              <span style={{ fontWeight: 700, color: "#1E293B" }}>{value}</span>
            </div>
          ))}
        </div>

        {/* Warning note */}
        <div style={{
          background: "#FEF2F2", border: "1px solid #FECACA",
          borderRadius: 12, padding: "12px 14px",
          fontSize: 13, color: "#DC2626", marginBottom: 20, lineHeight: 1.5
        }}>
          ⚠️ The user will <strong>lose access</strong> to their plan immediately.
          No automatic refund is issued.
        </div>

        {/* Buttons */}
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} disabled={loading} style={{
            flex: 1, padding: "12px", borderRadius: 12,
            border: "1px solid #CBD5E1", background: "#fff",
            cursor: "pointer", fontWeight: 700, fontSize: 14, color: "#64748B",
            fontFamily: "inherit"
          }}>Keep Active</button>
          <button onClick={onConfirm} disabled={loading} style={{
            flex: 1, padding: "12px", borderRadius: 12, border: "none",
            background: loading ? "#FCA5A5" : "#DC2626",
            color: "#fff", cursor: loading ? "not-allowed" : "pointer",
            fontWeight: 800, fontSize: 14, fontFamily: "inherit"
          }}>
            {loading ? "Cancelling..." : "Yes, Cancel It"}
          </button>
        </div>
      </div>
    </>
  );
}

export default function Subscriptions() {
  const [subs,         setSubs]         = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [status,       setStatus]       = useState("");
  const [search,       setSearch]       = useState("");
  const [page,         setPage]         = useState(1);
  const [pagination,   setPagination]   = useState(null);
  const [selected,     setSelected]     = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null); // sub object to cancel
  const [cancelling,   setCancelling]   = useState(false);

  const fmt = (n) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(n || 0);
  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";
  const daysLeft = (end) => {
    if (!end) return null;
    const diff = Math.ceil((new Date(end) - new Date()) / (1000*60*60*24));
    return diff;
  };

  const fetchSubs = async (p = 1, s = "", q = "") => {
    try {
      setLoading(true);
      const data = await getSubscriptions({ page: p, limit: 20, status: s, search: q });
      setSubs(data.subscriptions);
      setPagination(data.pagination);
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to load");
    } finally { setLoading(false); }
  };

  // Re-fetch whenever page, status OR search changes
  useEffect(() => { fetchSubs(page, status, search); }, [page, status, search]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    // fetchSubs called automatically by useEffect above
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await cancelSubscription(cancelTarget.id);
      setCancelTarget(null);
      fetchSubs(page, status, search);
    } catch (err) {
      alert(err?.response?.data?.message || "Cancel failed");
    } finally {
      setCancelling(false);
    }
  };

  const activeCount    = subs.filter(s => s.status === "active").length;
  const expiringCount  = subs.filter(s => {
    const d = daysLeft(s.end);
    return s.status === "active" && d !== null && d <= 7;
  }).length;

  return (
    <div className="adm-layout">
      <Sidebar />
      <main className="adm-main">
        <div className="adm-shell">
          <Topbar title="Subscriptions" subtitle="All plans — instant activation, no admin approval needed" />

          {/* ── Info note ── */}
          <div style={{
            display: "flex", gap: 12, alignItems: "flex-start",
            background: "#EFF6FF", border: "1px solid #BFDBFE",
            borderRadius: 14, padding: "14px 18px", marginBottom: 20,
          }}>
            <span style={{ fontSize: 20 }}>⚡</span>
            <div>
              <strong style={{ fontSize: 14, color: "#1E40AF" }}>Instant Activation</strong>
              <p style={{ margin: "3px 0 0", fontSize: 13, color: "#3B82F6", lineHeight: 1.5 }}>
                Users' subscriptions activate automatically the moment Razorpay payment succeeds.
                No admin confirmation step. You can cancel active subscriptions if needed.
              </p>
            </div>
          </div>

          {/* ── Summary strip ── */}
          {pagination && (
            <div style={{ display: "grid", gridTemplateColumns:"repeat(3,1fr)", gap: 10, marginBottom: 18 }}>
              {[
                { label: "Total",          val: pagination.total, bg: "#EFF6FF", color: "#1E40AF" },
                { label: "Active",         val: activeCount,      bg: "#D1FAE5", color: "#065F46" },
                { label: "Expiring ≤7d",   val: expiringCount,    bg: "#FEF3C7", color: "#92400E" },
              ].map((s, i) => (
                <div key={i} style={{
                  background: s.bg, color: s.color,
                  padding: "12px 14px", borderRadius: 12,
                  fontWeight: 800, fontSize: 22, textAlign:"center"
                }}>
                  {s.val}
                  <div style={{ fontWeight: 600, fontSize: 11, marginTop:2, opacity:.8 }}>{s.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* ── Filters ── */}
          <div style={{ display: "flex", flexDirection:"column", gap: 10, marginBottom: 16 }}>
            <div style={{ display:"flex", gap:8, overflowX:"auto", scrollbarWidth:"none", paddingBottom:2 }}>
              {["", "active", "expired", "cancelled"].map(s => (
                <button key={s}
                  onClick={() => { setStatus(s); setPage(1); }}
                  style={{
                    padding: "7px 14px", borderRadius: 20, cursor: "pointer",
                    border: "none", whiteSpace:"nowrap", flexShrink:0,
                    background: status === s ? "#1F4E8C" : "#F1F5F9",
                    color: status === s ? "#fff" : "#475569",
                    fontWeight: 700, fontSize: 13, fontFamily: "inherit",
                  }}>
                  {s === "" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                placeholder="🔍 Search by name or phone..."
                style={{ padding: "9px 14px", borderRadius: 10, border: "1px solid #CBD5E1",
                  fontSize: 13, flex:1, outline:"none", fontFamily: "inherit" }}
              />
              {search && (
                <button onClick={() => { setSearch(""); setPage(1); }} style={{
                  padding: "9px 14px", borderRadius: 10, border: "1px solid #CBD5E1",
                  background: "#F1F5F9", color: "#64748B", fontWeight: 700,
                  cursor: "pointer", fontFamily: "inherit", flexShrink:0
                }}>✕ Clear</button>
              )}
            </div>
          </div>

          <div className="adm-panel" style={{ gridColumn: "span 12" }}>
            <h3 style={{ margin: "0 0 16px" }}>
              Subscriptions {pagination ? `(${pagination.total} total)` : ""}
            </h3>

            {loading ? (
              <div style={{ padding: 20, color: "var(--muted-neutral)" }}>Loading...</div>
            ) : subs.length === 0 ? (
              <div style={{ padding: 20, color: "#64748B", textAlign: "center" }}>No subscriptions found.</div>
            ) : (
              <>
                {/* ── Desktop table ── */}
                <div className="sub-table-wrap">
                <table className="adm-table">
                  <thead>
                    <tr>
                      <th>ID</th><th>User</th><th>Phone</th><th>Plan</th><th>Amount</th><th>Start</th><th>Expires</th><th>Days Left</th><th>Status</th><th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subs.map(s => {
                      const dl  = daysLeft(s.end);
                      const cfg = STATUS_COLORS[s.status] || STATUS_COLORS.cancelled;
                      const expiringSoon = s.status === "active" && dl !== null && dl <= 7;
                      return (
                        <tr key={s.id} style={expiringSoon?{background:"#FFFBEB"}:{}}>
                          <td style={{ fontWeight: 700, color: "var(--base-primary)" }}>#{s.id}</td>
                          <td><strong>{s.user}</strong></td>
                          <td>{s.phone}</td>
                          <td>{s.plan}</td>
                          <td style={{ fontWeight: 700 }}>{fmt(s.amount)}</td>
                          <td>{fmtDate(s.start)}</td>
                          <td>{fmtDate(s.end)}</td>
                          <td>{dl!==null&&s.status==="active"?<span style={{fontWeight:800,color:dl<=3?"#DC2626":dl<=7?"#D97706":"#059669"}}>{dl>0?`${dl}d`:"⚠ Exp"}</span>:"—"}</td>
                          <td><span className="adm-badge" style={{background:cfg.bg,color:cfg.color,fontWeight:800}}>{s.status==="active"?"✅ Active":s.status==="expired"?"⌛ Expired":"🚫 Cancelled"}</span></td>
                          <td>{s.status==="active"&&<button onClick={()=>setCancelTarget(s)} style={{padding:"4px 10px",borderRadius:6,cursor:"pointer",background:"#FEE2E2",color:"#DC2626",border:"none",fontWeight:700,fontSize:12,fontFamily:"inherit"}}>Cancel</button>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>

                {/* ── Mobile flow list ── */}
                <div className="sub-cards-wrap">
                  {subs.map(s => {
                    const dl  = daysLeft(s.end);
                    const cfg = STATUS_COLORS[s.status] || STATUS_COLORS.cancelled;
                    const expiringSoon = s.status==="active"&&dl!==null&&dl<=7;
                    const dlColor = dl<=3?"#DC2626":dl<=7?"#D97706":"#059669";
                    const barColor = s.status==="active"?"#1F4E8C":s.status==="expired"?"#D97706":"#94A3B8";
                    return (
                      <div key={s.id} className="mb-card"
                        style={{ marginBottom:12, background: expiringSoon?"#FFFBEB":"#fff" }}>

                        {/* Coloured top bar */}
                        <div className="mb-card-bar" style={{ background: barColor }}/>

                        {/* Title row */}
                        <div className="mb-card-top">
                          <div>
                            <div className="mb-bk-id">Sub #{s.id}</div>
                            <div className="mb-svc-name">{s.user}</div>
                            <div className="mb-svc-cat">📱 {s.phone}</div>
                          </div>
                          <div className="mb-card-right">
                            <span className="mb-status-badge" style={{ background:cfg.bg, color:cfg.color }}>
                              {s.status==="active"?"● Active":s.status==="expired"?"● Expired":"● Cancelled"}
                            </span>
                            <div className="mb-amount">{fmt(s.amount)}</div>
                          </div>
                        </div>

                        {/* Info grid */}
                        <div className="mb-info-row">
                          <div className="mb-info-item">
                            <span className="mb-info-icon">💳</span>
                            <div>
                              <div className="mb-info-label">Plan</div>
                              <div className="mb-info-val" style={{ color:"#1F4E8C" }}>{s.plan}</div>
                            </div>
                          </div>
                          <div className="mb-info-item">
                            <span className="mb-info-icon">⏳</span>
                            <div>
                              <div className="mb-info-label">Days Left</div>
                              <div className="mb-info-val" style={{ color: s.status==="active"?dlColor:"#94A3B8" }}>
                                {s.status==="active"&&dl!==null?(dl>0?`${dl} days`:"Expired"):"—"}
                              </div>
                            </div>
                          </div>
                          <div className="mb-info-item">
                            <span className="mb-info-icon">📅</span>
                            <div>
                              <div className="mb-info-label">Started</div>
                              <div className="mb-info-val">{fmtDate(s.start)}</div>
                            </div>
                          </div>
                          <div className="mb-info-item">
                            <span className="mb-info-icon">🗓</span>
                            <div>
                              <div className="mb-info-label">Expires</div>
                              <div className="mb-info-val" style={{ color: expiringSoon?"#D97706":"#1E293B" }}>
                                {fmtDate(s.end)}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Footer */}
                        <div className="mb-card-footer">
                          <div className="mb-booked-on">
                            {expiringSoon&&<span style={{ color:"#D97706", fontWeight:700 }}>⚠ Expiring soon · </span>}
                            {s.plan}
                          </div>
                          {s.status==="active"&&(
                            <button onClick={()=>setCancelTarget(s)} style={{
                              padding:"6px 14px", borderRadius:20, border:"none",
                              background:"#FEE2E2", color:"#DC2626",
                              fontWeight:800, fontSize:12, cursor:"pointer", fontFamily:"inherit"
                            }}>Cancel</button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {pagination?.totalPages > 1 && (
                  <div style={{ marginTop: 16, display: "flex", gap: 8, alignItems: "center" }}>
                    <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page===1}
                      style={{ padding: "7px 14px", borderRadius: 8, cursor: "pointer", border: "1px solid #ddd", fontFamily: "inherit" }}>
                      ← Prev
                    </button>
                    <span style={{ fontSize: 13, color: "#64748B" }}>Page {page} of {pagination.totalPages}</span>
                    <button onClick={() => setPage(p => Math.min(pagination.totalPages, p+1))} disabled={page===pagination.totalPages}
                      style={{ padding: "7px 14px", borderRadius: 8, cursor: "pointer", border: "1px solid #ddd", fontFamily: "inherit" }}>
                      Next →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>


      <CancelModal
        sub={cancelTarget}
        onConfirm={handleCancel}
        onClose={() => setCancelTarget(null)}
        loading={cancelling}
      />
    </div>
  );
}